import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * PasswordInput — controlled password field with a show/hide toggle.
 *
 * Why this exists:
 *  - Browsers render literal `placeholder="••••••••"` text with the password
 *    mask font, so the field looks pre-filled even when empty. We use plain
 *    text placeholders instead.
 *  - Toggling `type` between "password" and "text" gives a working reveal eye
 *    icon without re-mounting the input (preserves focus + caret position).
 *
 * Props mirror a normal <input>: `value`, `onChange`, `placeholder`,
 * `autoComplete`, `id`, `name`, `required`, `data-testid`, plus `className`
 * and `disabled`. Extra props are forwarded to the underlying <Input>.
 */
export default function PasswordInput({
  value,
  onChange,
  placeholder = "Enter your password",
  autoComplete = "current-password",
  className = "",
  disabled = false,
  id,
  name,
  required = false,
  "data-testid": dataTestid,
  ...rest
}) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;
  const toggleLabel = visible ? "Hide password" : "Show password";

  const toggleTestid = dataTestid ? `${dataTestid}-toggle` : "password-toggle";

  return (
    <div className="relative">
      <Input
        id={id}
        name={name}
        type={visible ? "text" : "password"}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
        // Right padding keeps text from sliding under the toggle button.
        className={cn(
          "h-12 rounded-full border-border bg-pp-surface px-5 pr-12",
          className
        )}
        data-testid={dataTestid}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={toggleLabel}
        title={toggleLabel}
        tabIndex={0}
        className={cn(
          "absolute right-1.5 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-pp-text transition-colors",
          "hover:text-brand-orange focus:text-brand-orange",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-1"
        )}
        data-testid={toggleTestid}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
